import React, { useEffect, useMemo, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader, Card, Section } from "@/components/Primitives";
import SearchSelect from "@/components/SearchSelect";
import { toast } from "sonner";
import {
  Target as TargetIcon, Plus, PencilSimpleLine, Trash, Broom, ArrowsCounterClockwise,
  MapPin, UserCircle, UploadSimple, CheckCircle, WarningCircle, FileCsv,
} from "@phosphor-icons/react";

const YEARS = [2025, 2026];
const TYPES = [
  { k: "location", l: "Location", icon: MapPin },
  { k: "salesman", l: "Salesman", icon: UserCircle },
];

const emptyForm = (year = 2026) => ({
  year, target_type: "location",
  company_code: "",
  location_code: "", location_name: "",
  salesman_code: "", salesman_name: "",
  target_1: 0, target_2: 0, target_3: 0,
  period: "yearly",
  monthly_target_1: Array(12).fill(0),
  monthly_target_2: Array(12).fill(0),
  monthly_target_3: Array(12).fill(0),
});

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Reusable monthly schedule editor.
// Renders a clean 12-row × 3-column table (Tier 1 / Tier 2 / Tier 3) with
// per-tier "Annual ÷ 12" quick-fill, and a footer total row. Designed to
// replace the cramped horizontal grid that crashed visually on narrow side
// panels.
const MonthlySchedule = ({ value, onChange, testIdPrefix }) => {
  const tiers = [
    { id: 1, label: "Target 1 · Min", key: "monthly_target_1", color: "muted" },
    { id: 2, label: "Target 2 · Plan", key: "monthly_target_2", color: "myb" },
    { id: 3, label: "Target 3 · Stretch", key: "monthly_target_3", color: "muted" },
  ];

  const sum = (arr) => (arr || []).reduce((a, b) => a + (Number(b) || 0), 0);
  const setCell = (key, idx, v) => {
    const arr = [...(value[key] || Array(12).fill(0))];
    arr[idx] = v;
    onChange({ ...value, [key]: arr });
  };
  const fillEqual = (key) => {
    const annual = Number(window.prompt(`Annual amount for ${key.replace("monthly_", "").replace("_", " ")} — split equally across 12 months:`, "0"));
    if (!annual || annual <= 0) return;
    const each = Number((annual / 12).toFixed(2));
    onChange({ ...value, [key]: Array(12).fill(each) });
  };
  const fillSeasonal = (key) => {
    // Lightweight seasonal preset: heavier in Ramadan-ish, mid-year dip, big Q4.
    // Total weight = 12 → each weight × (annual/12) = monthly amount.
    const annual = Number(window.prompt(`Annual amount for ${key.replace("monthly_", "").replace("_", " ")} — apply seasonal preset:`, "0"));
    if (!annual || annual <= 0) return;
    const weights = [1.0, 1.1, 1.2, 1.0, 0.9, 0.85, 0.85, 0.9, 0.95, 1.05, 1.15, 1.05]; // sums to 12
    const arr = weights.map((w) => Number(((annual / 12) * w).toFixed(2)));
    onChange({ ...value, [key]: arr });
  };

  return (
    <div data-testid={`${testIdPrefix}-monthly-grid`} className="space-y-3">
      <div className="text-[11px] text-muted-foreground leading-relaxed">
        Enter a value per month for each tier. Use <span className="font-semibold">Equal</span> or <span className="font-semibold">Seasonal</span> to bulk-fill from an annual amount, then tweak individual months for Ramadan / DSF / Christmas spikes.
      </div>

      {/* Per-tier quick-fill buttons */}
      <div className="grid grid-cols-3 gap-2 text-[10px]">
        {tiers.map((t) => (
          <div key={t.id} className="border hairline rounded-sm p-2 bg-myb-50/40">
            <div className={`uppercase tracking-[0.18em] font-semibold mb-1.5 ${t.color === "myb" ? "text-myb-500" : "text-muted-foreground"}`}>
              {t.label}{t.id === 2 ? " *" : ""}
            </div>
            <div className="text-[10px] tabular-nums text-foreground mb-2">Sum: <span className="font-semibold">{fmtNumber(sum(value[t.key]))}</span></div>
            <div className="flex gap-1">
              <button type="button" onClick={() => fillEqual(t.key)} data-testid={`${testIdPrefix}-fill-equal-t${t.id}`}
                      className="flex-1 px-1.5 py-1 border hairline rounded-sm hover:border-myb-500 hover:text-myb-500 transition-all">Equal</button>
              <button type="button" onClick={() => fillSeasonal(t.key)} data-testid={`${testIdPrefix}-fill-seasonal-t${t.id}`}
                      className="flex-1 px-1.5 py-1 border hairline rounded-sm hover:border-myb-500 hover:text-myb-500 transition-all">Seasonal</button>
            </div>
          </div>
        ))}
      </div>

      {/* Schedule table — 12 rows × 3 tiers */}
      <div className="border hairline rounded-sm overflow-hidden">
        <div className="grid grid-cols-[68px_1fr_1fr_1fr] bg-myb-50/60 text-[10px] uppercase tracking-[0.18em] font-semibold text-myb-500">
          <div className="px-3 py-2">Month</div>
          {tiers.map((t) => (
            <div key={t.id} className={`px-3 py-2 text-right ${t.color === "myb" ? "text-myb-500" : "text-muted-foreground"}`}>
              T{t.id}{t.id === 2 ? " *" : ""}
            </div>
          ))}
        </div>
        {MONTH_LABELS.map((m, idx) => (
          <div key={m} className="grid grid-cols-[68px_1fr_1fr_1fr] border-t hairline items-center text-sm">
            <div className="px-3 py-1.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold">{m}</div>
            {tiers.map((t) => (
              <div key={t.id} className="px-2 py-1">
                <input
                  data-testid={`${testIdPrefix}-monthly-t${t.id}-${idx}`}
                  type="number" min="0" step="0.01"
                  value={(value[t.key] || Array(12).fill(0))[idx] ?? 0}
                  onChange={(e) => setCell(t.key, idx, e.target.value)}
                  placeholder="0"
                  className={`w-full bg-white border focus:border-myb-500 rounded-sm px-2 py-1.5 text-sm outline-none tabular-nums text-right ${t.color === "myb" ? "border-myb-500/40" : "border-border"}`}
                />
              </div>
            ))}
          </div>
        ))}
        <div className="grid grid-cols-[68px_1fr_1fr_1fr] bg-myb-50/40 border-t hairline text-[11px] font-semibold">
          <div className="px-3 py-2 uppercase tracking-[0.18em] text-myb-500">Annual</div>
          {tiers.map((t) => (
            <div key={t.id} className={`px-4 py-2 text-right tabular-nums ${t.color === "myb" ? "text-myb-500" : "text-foreground"}`}>
              {fmtNumber(sum(value[t.key]))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Targets = () => {
  const { user } = useAuth();
  const canManage = hasRole(user, "super_admin", "management");
  const isSuperAdmin = hasRole(user, "super_admin");
  const [year, setYear] = useState(2026);
  const [type, setType] = useState("location");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [form, setForm] = useState(emptyForm(2026));
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const previewBulk = async () => {
    if (!bulkFile) return;
    setBulkBusy(true);
    try {
      const fd = new FormData(); fd.append("file", bulkFile);
      const { data } = await api.post("/targets/preview-file", fd);
      setBulkPreview(data);
      if (!data.rows.length) toast.info("No valid rows detected in file.");
      else toast.success(`Parsed ${data.rows.length} row(s), ${data.errors.length} error(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBulkBusy(false); }
  };

  const commitBulk = async () => {
    if (!bulkPreview?.rows?.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/targets/bulk", { targets: bulkPreview.rows });
      toast.success(`Saved · inserted ${data.inserted}, updated ${data.updated}`);
      setBulkOpen(false); setBulkFile(null); setBulkPreview(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBulkBusy(false); }
  };

  const downloadTemplate = () => {
    const rows = [
      "year,type,location_code,location_name,salesman_code,salesman_name,target_1,target_2,target_3",
      "2026,location,234,APM Monaco Btq Avenues,,,3150000",
      "2026,salesman,234,APM Monaco Btq Avenues,6659,Samir Betka,750000",
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "targets_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const load = async () => {
    const [lst, sm] = await Promise.all([
      api.get(`/targets?year=${year}&target_type=${type}`),
      api.get(`/targets/summary?year=${year}`),
    ]);
    setRows(lst.data.rows || []);
    setSummary(sm.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, type]);

  // Cascading reference data for the form selectors. Whenever the user
  // picks a company, the location list narrows to that company's boutiques.
  // Likewise when a location is picked, the salesman list narrows to the
  // staff who actually transacted at that location. Picking a salesman has
  // no further dependents (it's the leaf).
  useEffect(() => {
    const ctrl = new AbortController();
    api.get("/ref/companies", { signal: ctrl.signal })
      .then((r) => setCompanies(r.data.rows || []))
      .catch(() => {});
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    const qp = new URLSearchParams();
    if (form.company_code) qp.append("company", form.company_code);
    const qs = qp.toString() ? `?${qp.toString()}` : "";
    api.get(`/ref/locations${qs}`, { signal: ctrl.signal })
      .then((r) => setLocations(r.data.rows || []))
      .catch(() => {});
    return () => ctrl.abort();
  }, [form.company_code]);

  useEffect(() => {
    const ctrl = new AbortController();
    const qp = new URLSearchParams();
    if (form.company_code) qp.append("company", form.company_code);
    if (form.location_code) qp.append("location_code", form.location_code);
    const qs = qp.toString() ? `?${qp.toString()}` : "";
    api.get(`/ref/salesmen${qs}`, { signal: ctrl.signal })
      .then((r) => setSalesmen(r.data.rows || []))
      .catch(() => {});
    return () => ctrl.abort();
  }, [form.company_code, form.location_code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.location_code) { toast.error("Pick a location"); return; }
    if (form.target_type === "salesman" && !form.salesman_code) { toast.error("Pick a salesman"); return; }
    const isMonthly = form.period === "monthly";
    let t1, t2, t3;
    if (isMonthly) {
      t1 = (form.monthly_target_1 || []).reduce((a, b) => a + (Number(b) || 0), 0);
      t2 = (form.monthly_target_2 || []).reduce((a, b) => a + (Number(b) || 0), 0);
      t3 = (form.monthly_target_3 || []).reduce((a, b) => a + (Number(b) || 0), 0);
    } else {
      t1 = Number(form.target_1) || 0;
      t2 = Number(form.target_2) || 0;
      t3 = Number(form.target_3) || 0;
    }
    if (t2 <= 0) { toast.error("Enter positive Target 2 value(s)"); return; }
    if (t1 && t1 > t2) { toast.error("Target 1 cannot exceed Target 2"); return; }
    if (t3 && t3 < t2) { toast.error("Target 3 cannot be less than Target 2"); return; }
    setBusy(true);
    try {
      const loc = locations.find((l) => String(l.code) === String(form.location_code));
      const sm = salesmen.find((s) => String(s.code) === String(form.salesman_code));
      const { company_code: _omit, ...formPayload } = form;
      const payload = {
        ...formPayload,
        target_1: t1,
        target_2: t2,
        target_3: t3,
        target_amount: t2,
        location_name: loc?.name || form.location_name,
        salesman_name: sm?.name || form.salesman_name,
        period: form.period,
      };
      if (isMonthly) {
        payload.monthly_target_1 = (form.monthly_target_1 || []).map(Number);
        payload.monthly_target_2 = (form.monthly_target_2 || []).map(Number);
        payload.monthly_target_3 = (form.monthly_target_3 || []).map(Number);
      } else {
        // Don't store stale monthly arrays when saving in yearly mode
        delete payload.monthly_target_1;
        delete payload.monthly_target_2;
        delete payload.monthly_target_3;
      }
      await api.post("/targets", payload);
      toast.success(`Target saved (${isMonthly ? "monthly" : "yearly"})`);
      setForm(emptyForm(year));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const isMonthly = editing.period === "monthly";
      const payload = isMonthly
        ? {
            period: "monthly",
            monthly_target_1: (editing.monthly_target_1 || []).map((v) => Number(v) || 0),
            monthly_target_2: (editing.monthly_target_2 || []).map((v) => Number(v) || 0),
            monthly_target_3: (editing.monthly_target_3 || []).map((v) => Number(v) || 0),
          }
        : {
            period: "yearly",
            target_1: Number(editing.target_1) || 0,
            target_2: Number(editing.target_2) || 0,
            target_3: Number(editing.target_3) || 0,
          };
      await api.patch(`/targets/${editing.id}`, payload);
      toast.success("Target updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this target?")) return;
    try { await api.delete(`/targets/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reseed = async () => {
    if (!window.confirm(`Replace all ${year} location targets by re-reading raw transactions?`)) return;
    try {
      const { data } = await api.post(`/targets/reseed?year=${year}`);
      if (!data.ok) toast.info(data.message || "Nothing to seed");
      else toast.success(`Seeded ${data.inserted} location targets for ${year}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cleanup = async () => {
    if (!window.confirm("Remove target_1/2/3 fields from all sales transactions? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/targets/cleanup-transactions");
      toast.success(`Cleaned ${data.modified} rows`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.location_name || "").toLowerCase().includes(q) ||
      (r.location_code || "").includes(q) ||
      (r.salesman_name || "").toLowerCase().includes(q) ||
      (r.salesman_code || "").includes(q),
    );
  }, [rows, search]);

  return (
    <div>
      <PageHeader
        eyebrow="Target Center"
        title="Set the bar"
        subtitle="Annual sales targets by location and salesman. Used across dashboards, reports, and PNG exports."
      />

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6 no-print">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Year</span>
          <div className="flex border hairline rounded-sm overflow-hidden">
            {YEARS.map((y) => (
              <button
                key={y}
                data-testid={`targets-year-${y}`}
                onClick={() => setYear(y)}
                className={`text-xs px-3 py-1.5 uppercase tracking-wider font-semibold transition-all ${
                  year === y ? "bg-myb-500 text-white" : "text-muted-foreground hover:text-myb-500 hover:bg-myb-50"
                }`}
              >{y}</button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Type</span>
          <div className="flex border hairline rounded-sm overflow-hidden">
            {TYPES.map((t) => (
              <button
                key={t.k}
                data-testid={`targets-type-${t.k}`}
                onClick={() => setType(t.k)}
                className={`text-xs px-3 py-1.5 uppercase tracking-wider font-semibold transition-all inline-flex items-center gap-1 ${
                  type === t.k ? "bg-myb-500 text-white" : "text-muted-foreground hover:text-myb-500 hover:bg-myb-50"
                }`}
              ><t.icon size={12} /> {t.l}</button>
            ))}
          </div>
        </div>

        <input
          data-testid="targets-search"
          value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by location or salesman…"
          className="flex-1 max-w-sm bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2 text-sm outline-none"
        />

        {canManage && (
          <>
            <button
              data-testid="targets-bulk-btn"
              onClick={() => { setBulkOpen(true); setBulkPreview(null); setBulkFile(null); }}
              className="text-xs uppercase tracking-[0.22em] bg-myb-500 text-white hover:bg-myb-600 px-3 py-2 rounded-sm font-semibold inline-flex items-center gap-2"
            ><UploadSimple size={12} /> Bulk upload</button>
            <button
              data-testid="targets-reseed-btn"
              onClick={reseed}
              className="text-xs uppercase tracking-[0.22em] border border-myb-200 hover:border-myb-500 hover:text-myb-500 text-muted-foreground px-3 py-2 rounded-sm font-semibold inline-flex items-center gap-2"
            ><ArrowsCounterClockwise size={12} /> Reseed from data</button>
            {isSuperAdmin && (
              <button
                data-testid="targets-cleanup-btn"
                onClick={cleanup}
                className="text-xs uppercase tracking-[0.22em] border border-amber-300 text-amber-700 hover:bg-amber-50 px-3 py-2 rounded-sm font-semibold inline-flex items-center gap-2"
              ><Broom size={12} /> Cleanup transactions</button>
            )}
          </>
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-6">
          <Card title={`Location targets · ${summary.year}`} testId="targets-summary-location">
            <div className="font-display text-4xl font-light text-myb-500 leading-none">{fmtNumber(summary.location.count)}</div>
            <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider font-semibold">Entries</div>
          </Card>
          <Card title={`Sum of location targets`}>
            <div className="font-display text-4xl font-light text-foreground leading-none">{fmtNumber(summary.location.total_amount)}</div>
            <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider font-semibold">KWD</div>
          </Card>
          <Card title={`Salesman targets · ${summary.year}`} testId="targets-summary-salesman">
            <div className="font-display text-4xl font-light text-myb-500 leading-none">{fmtNumber(summary.salesman.count)}</div>
            <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider font-semibold">Entries</div>
          </Card>
          <Card title={`Sum of salesman targets`}>
            <div className="font-display text-4xl font-light text-foreground leading-none">{fmtNumber(summary.salesman.total_amount)}</div>
            <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider font-semibold">KWD</div>
          </Card>
        </div>
      )}

      <div className={`grid grid-cols-1 ${form.period === "monthly" ? "lg:grid-cols-2" : "lg:grid-cols-3"} gap-6`}>
        {/* Table */}
        <Card className={form.period === "monthly" ? "" : "lg:col-span-2"} title={`${type === "location" ? "Location" : "Salesman"} targets · ${year}`} testId="targets-table">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">No targets yet for this filter.</div>
          ) : (
            <div className="overflow-auto scrollbar-thin max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white/95">
                  <tr className="text-left border-b hairline">
                    {(type === "location"
                      ? ["Location", "Code", "Period", "Target 1", "Target 2", "Target 3", ""]
                      : ["Location", "Salesman", "Code", "Period", "Target 1", "Target 2", "Target 3", ""]
                    ).map((h) => (
                      <th key={h} className="py-2 px-3 text-[10px] uppercase tracking-[0.2em] text-myb-500 font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b hairline hover:bg-myb-50/50" data-testid={`targets-row-${r.id}`}>
                      <td className="py-2 px-3">{r.location_name || "—"}</td>
                      {type === "salesman" && <td className="py-2 px-3">{r.salesman_name || "—"}</td>}
                      <td className="py-2 px-3 font-mono text-xs text-muted-foreground">
                        {type === "salesman" ? r.salesman_code : r.location_code}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-block text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${
                          r.period === "monthly"
                            ? "bg-myb-50 text-myb-500 border-myb-200"
                            : "bg-muted/40 text-muted-foreground border-border"
                        }`}>
                          {r.period === "monthly" ? "Monthly" : "Yearly"}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">{r.target_1 ? fmtNumber(r.target_1) : "—"}</td>
                      <td className="py-2 px-3 font-medium">{fmtNumber(r.target_2 || r.target_amount)}</td>
                      <td className="py-2 px-3 text-muted-foreground">{r.target_3 ? fmtNumber(r.target_3) : "—"}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {canManage && (
                          <>
                            <button
                              data-testid={`targets-edit-${r.id}`}
                              onClick={() => setEditing({ ...r })}
                              className="text-myb-500 hover:text-myb-600 p-1 mr-1"
                            ><PencilSimpleLine size={14} /></button>
                            <button
                              data-testid={`targets-delete-${r.id}`}
                              onClick={() => del(r.id)}
                              className="text-rose-500/80 hover:text-rose-600 p-1"
                            ><Trash size={14} /></button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add form */}
        {canManage && (
          <Card title="Add / update target" testId="targets-add-form">
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Year</label>
                <select data-testid="targets-form-year" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
                        className="mt-1 w-full bg-white border border-border rounded-sm px-3 py-2.5 text-sm outline-none">
                  {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Type</label>
                <select data-testid="targets-form-type" value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value })}
                        className="mt-1 w-full bg-white border border-border rounded-sm px-3 py-2.5 text-sm outline-none">
                  <option value="location">Location</option>
                  <option value="salesman">Salesman</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Company</label>
                <SearchSelect
                  testId="targets-form-company"
                  width="w-full"
                  height="h-10"
                  className="mt-1"
                  placeholder="All companies"
                  allLabel="All companies"
                  value={form.company_code}
                  onChange={(v) => setForm({ ...form, company_code: v, location_code: "", salesman_code: "" })}
                  options={companies.map((c) => ({ value: String(c.code), label: c.name, sub: c.code }))}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Location</label>
                <SearchSelect
                  testId="targets-form-location"
                  width="w-full"
                  height="h-10"
                  className="mt-1"
                  placeholder="Choose…"
                  value={form.location_code}
                  onChange={(v) => setForm({ ...form, location_code: v, salesman_code: "" })}
                  options={locations.map((l) => ({ value: String(l.code), label: l.name || l.code, sub: l.code }))}
                />
              </div>
              {form.target_type === "salesman" && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Salesman</label>
                  <SearchSelect
                    testId="targets-form-salesman"
                    width="w-full"
                    height="h-10"
                    className="mt-1"
                    placeholder="Choose…"
                    value={form.salesman_code}
                    onChange={(v) => setForm({ ...form, salesman_code: v })}
                    options={salesmen.map((s) => ({ value: String(s.code), label: s.name || s.code, sub: s.code }))}
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Period</label>
                <div className="mt-1 inline-flex border hairline rounded-sm overflow-hidden w-full" data-testid="targets-form-period">
                  {[
                    { k: "yearly", label: "Yearly · split equally" },
                    { k: "monthly", label: "Monthly · custom 12" },
                  ].map(({ k, label }) => (
                    <button
                      key={k} type="button"
                      data-testid={`targets-form-period-${k}`}
                      onClick={() => setForm({ ...form, period: k })}
                      className={`flex-1 text-[11px] uppercase tracking-wider px-3 py-2 font-semibold transition-all ${
                        form.period === k ? "bg-myb-500 text-white" : "text-muted-foreground hover:bg-myb-50"
                      }`}
                    >{label}</button>
                  ))}
                </div>
              </div>

              {form.period === "yearly" ? (
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Target 1</label>
                    <input data-testid="targets-form-t1" type="number" min="0" step="0.01"
                          value={form.target_1}
                          onChange={(e) => setForm({ ...form, target_1: e.target.value })}
                          className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-2 py-2 text-sm outline-none"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold">Target 2 *</label>
                    <input data-testid="targets-form-t2" type="number" min="0" step="0.01" required
                          value={form.target_2}
                          onChange={(e) => setForm({ ...form, target_2: e.target.value })}
                          className="mt-1 w-full bg-white border border-myb-500/60 focus:border-myb-500 rounded-sm px-2 py-2 text-sm outline-none"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Target 3</label>
                    <input data-testid="targets-form-t3" type="number" min="0" step="0.01"
                          value={form.target_3}
                          onChange={(e) => setForm({ ...form, target_3: e.target.value })}
                          className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-2 py-2 text-sm outline-none"/>
                  </div>
                </div>
              ) : (
                <MonthlySchedule
                  testIdPrefix="targets-form"
                  value={form}
                  onChange={(v) => setForm({ ...form, ...v })}
                />
              )}
              <button data-testid="targets-form-submit" disabled={busy}
                      className="w-full inline-flex items-center justify-center gap-2 bg-myb-500 text-white hover:bg-myb-600 rounded-sm py-2.5 text-sm disabled:opacity-50">
                <Plus size={14} /> {busy ? "Saving…" : "Save target"}
              </button>
              <div className="text-[11px] text-muted-foreground">Creating a target for the same (year, type, location [+ salesman]) updates it.</div>
            </form>
          </Card>
        )}
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 flex" data-testid="targets-edit-panel">
          <div className="flex-1 bg-black/40" onClick={() => setEditing(null)} />
          <div className="w-full max-w-2xl bg-white h-full border-l hairline shadow-2xl flex flex-col">
            <div className="p-5 border-b hairline">
              <div className="text-[10px] uppercase tracking-[0.3em] text-myb-500 font-semibold">Edit target</div>
              <div className="font-display text-xl font-light mt-1">{editing.location_name}{editing.salesman_name ? ` · ${editing.salesman_name}` : ""}</div>
              <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider font-semibold">Year {editing.year} · {editing.target_type}</div>
            </div>
            <div className="p-5 space-y-3 flex-1 overflow-auto">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Period</label>
                <div className="mt-1 inline-flex border hairline rounded-sm overflow-hidden w-full" data-testid="targets-edit-period">
                  {[
                    { k: "yearly", label: "Yearly" },
                    { k: "monthly", label: "Monthly · 12 values" },
                  ].map(({ k, label }) => (
                    <button
                      key={k} type="button"
                      data-testid={`targets-edit-period-${k}`}
                      onClick={() => {
                        const monthly_target_1 = editing.monthly_target_1 || Array(12).fill(0);
                        const monthly_target_2 = editing.monthly_target_2 || Array(12).fill(0);
                        const monthly_target_3 = editing.monthly_target_3 || Array(12).fill(0);
                        setEditing({ ...editing, period: k, monthly_target_1, monthly_target_2, monthly_target_3 });
                      }}
                      className={`flex-1 text-[11px] uppercase tracking-wider px-3 py-2 font-semibold transition-all ${
                        (editing.period || "yearly") === k ? "bg-myb-500 text-white" : "text-muted-foreground hover:bg-myb-50"
                      }`}
                    >{label}</button>
                  ))}
                </div>
              </div>

              {(editing.period || "yearly") === "yearly" ? (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Target 1</label>
                    <input data-testid="targets-edit-t1" type="number" min="0" step="0.01"
                          value={editing.target_1 ?? 0}
                          onChange={(e) => setEditing({ ...editing, target_1: e.target.value })}
                          className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold">Target 2 *</label>
                    <input data-testid="targets-edit-t2" type="number" min="0" step="0.01"
                          value={editing.target_2 ?? editing.target_amount ?? 0}
                          onChange={(e) => setEditing({ ...editing, target_2: e.target.value })}
                          className="mt-1 w-full bg-white border border-myb-500/60 focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">Target 3</label>
                    <input data-testid="targets-edit-t3" type="number" min="0" step="0.01"
                          value={editing.target_3 ?? 0}
                          onChange={(e) => setEditing({ ...editing, target_3: e.target.value })}
                          className="mt-1 w-full bg-white border border-border focus:border-myb-500 rounded-sm px-3 py-2.5 text-sm outline-none"/>
                  </div>
                  <div className="text-[11px] text-muted-foreground">Target 2 is the core figure; Target 1 and Target 3 unlock richer progress views on the Dashboard.</div>
                </>
              ) : (
                <MonthlySchedule
                  testIdPrefix="targets-edit"
                  value={editing}
                  onChange={(v) => setEditing({ ...editing, ...v })}
                />
              )}
            </div>
            <div className="p-5 border-t hairline flex gap-2 justify-end">
              <button data-testid="targets-edit-cancel" onClick={() => setEditing(null)}
                      className="text-xs px-4 py-2.5 border border-border rounded-sm uppercase tracking-wider font-semibold text-muted-foreground hover:text-myb-500 hover:border-myb-500">Cancel</button>
              <button data-testid="targets-edit-save" onClick={saveEdit} disabled={busy}
                      className="text-xs px-5 py-2.5 bg-myb-500 text-white hover:bg-myb-600 rounded-sm uppercase tracking-wider font-semibold disabled:opacity-50">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk upload wizard */}
      {bulkOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center" data-testid="bulk-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => !bulkBusy && setBulkOpen(false)} />
          <div className="relative bg-white w-full max-w-3xl rounded-sm shadow-2xl border hairline overflow-hidden">
            <div className="p-5 border-b hairline flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-myb-500 font-semibold">Bulk Upload</div>
                <div className="font-display text-xl font-light mt-0.5">Import targets from CSV / Excel</div>
              </div>
              <button data-testid="bulk-close" onClick={() => setBulkOpen(false)} className="text-xs uppercase tracking-wider text-muted-foreground hover:text-myb-500">Close</button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div>Columns supported: <code className="bg-myb-50 px-1.5 py-0.5 text-myb-600 rounded-sm whitespace-nowrap">year, type, location_code, location_name, salesman_code, salesman_name, target_1, target_2, target_3</code></div>
                <button onClick={downloadTemplate} data-testid="bulk-template"
                        className="text-xs uppercase tracking-wider text-myb-500 hover:text-myb-600 inline-flex items-center gap-1 font-semibold">
                  <FileCsv size={12} /> Download template
                </button>
              </div>

              <label htmlFor="bulk-file" className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-myb-200 hover:border-myb-500 rounded-sm cursor-pointer transition-all bg-myb-50/30">
                <UploadSimple weight="duotone" size={36} className="text-myb-500 mb-2" />
                <div className="font-medium text-foreground">{bulkFile ? bulkFile.name : "Drop or choose a CSV / Excel file"}</div>
                <div className="text-xs text-muted-foreground mt-1">.csv · .xlsx · .xls</div>
                <input id="bulk-file" data-testid="bulk-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden"
                       onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkPreview(null); }} />
              </label>

              {bulkFile && !bulkPreview && (
                <div className="flex justify-end">
                  <button data-testid="bulk-preview-btn" onClick={previewBulk} disabled={bulkBusy}
                          className="text-xs px-4 py-2.5 bg-myb-500 text-white hover:bg-myb-600 rounded-sm uppercase tracking-wider font-semibold">
                    {bulkBusy ? "Parsing…" : "Preview"}
                  </button>
                </div>
              )}

              {bulkPreview && (
                <div data-testid="bulk-preview" className="space-y-3">
                  <div className="flex items-center gap-3 flex-wrap text-sm">
                    <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-0.5 text-xs"><CheckCircle size={14}/> {bulkPreview.rows.length} valid</span>
                    {bulkPreview.errors.length > 0 &&
                      <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-0.5 text-xs"><WarningCircle size={14}/> {bulkPreview.errors.length} rejected</span>}
                  </div>
                  {bulkPreview.errors.length > 0 && (
                    <div className="max-h-28 overflow-auto scrollbar-thin bg-amber-50/50 border border-amber-200 rounded-sm p-2 text-[11px] text-amber-800">
                      {bulkPreview.errors.slice(0, 10).map((e, i) => (
                        <div key={i}>Row {e.row}: {e.message}</div>
                      ))}
                    </div>
                  )}
                  <div className="max-h-64 overflow-auto border hairline rounded-sm scrollbar-thin">
                    <table className="w-full text-xs">
                      <thead className="bg-myb-50 sticky top-0">
                        <tr>
                          {["Year", "Type", "Location", "Salesman", "Target 1", "Target 2", "Target 3"].map((h) => (
                            <th key={h} className="text-left px-2 py-1.5 text-[10px] uppercase tracking-wider text-myb-500 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {bulkPreview.rows.slice(0, 100).map((r, i) => (
                          <tr key={i} className="odd:bg-myb-50/30">
                            <td className="px-2 py-1.5 border-t hairline">{r.year}</td>
                            <td className="px-2 py-1.5 border-t hairline">{r.target_type}</td>
                            <td className="px-2 py-1.5 border-t hairline">{r.location_name || "—"} <span className="text-muted-foreground">({r.location_code})</span></td>
                            <td className="px-2 py-1.5 border-t hairline">{r.salesman_name || "—"} {r.salesman_code ? <span className="text-muted-foreground">({r.salesman_code})</span> : ""}</td>
                            <td className="px-2 py-1.5 border-t hairline text-muted-foreground">{r.target_1 ? fmtNumber(r.target_1) : "—"}</td>
                            <td className="px-2 py-1.5 border-t hairline font-medium">{fmtNumber(r.target_2 || r.target_amount)}</td>
                            <td className="px-2 py-1.5 border-t hairline text-muted-foreground">{r.target_3 ? fmtNumber(r.target_3) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t hairline flex justify-end gap-2">
              <button data-testid="bulk-cancel" onClick={() => setBulkOpen(false)}
                      className="text-xs px-4 py-2.5 border border-border rounded-sm uppercase tracking-wider font-semibold text-muted-foreground hover:text-myb-500 hover:border-myb-500">Cancel</button>
              {bulkPreview && bulkPreview.rows.length > 0 && (
                <button data-testid="bulk-commit-btn" onClick={commitBulk} disabled={bulkBusy}
                        className="text-xs px-5 py-2.5 bg-myb-500 text-white hover:bg-myb-600 rounded-sm uppercase tracking-wider font-semibold disabled:opacity-50">
                  {bulkBusy ? "Saving…" : `Upsert ${bulkPreview.rows.length} target(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Targets;
